import * as React from "react";
import styled from "@emotion/styled";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import NestedMenuItem from "./NestedMenuItem";

export const Dropdown = React.forwardRef(
  (
    {
      trigger,
      menu,
      keepOpen: keepOpenGlobal,
      isOpen: controlledIsOpen,
      onOpen: onControlledOpen,
      minWidth,
      sx,
      isMobile = false
    },
    ref
  ) => {
    const [isInternalOpen, setInternalOpen] = React.useState(null);

    const isOpen = controlledIsOpen || isInternalOpen;

    let anchorRef = React.useRef(null);
    if (ref) {
      anchorRef = ref;
    }

    const handleOpen = (event) => {
      event.stopPropagation();

      if (menu.length) {
        onControlledOpen
          ? onControlledOpen(event.currentTarget)
          : setInternalOpen(event.currentTarget);
      }
    };

    const handleClose = (event) => {
      event.stopPropagation();

      if (anchorRef.current && anchorRef.current.contains(event.target)) {
        return;
      }

      handleForceClose();
    };

    const handleForceClose = () => {
      onControlledOpen ? onControlledOpen(null) : setInternalOpen(null);
    };

    const renderMenu = (menuItem, index) => {
      const { keepOpen: keepOpenLocal, ...props } = menuItem.props;

      let extraProps = {};
      if (props.menu) {
        extraProps = {
          parentMenuOpen: isOpen,
          isMobile: isMobile
        };
      }

      return React.createElement(menuItem.type, {
        ...props,
        key: index,
        ...extraProps,
        onClick: (event) => {
          // Check if this is a nested menu item (has submenu)
          const isNestedMenuItem = props.menu && props.menu.length > 0;
          
          if (isNestedMenuItem) {
            // For nested menu items, DON'T prevent default or stop propagation
            // Let the NestedMenuItem handle the event completely
            if (menuItem.props.onClick) {
              menuItem.props.onClick(event);
            }
          } else {
            // For regular menu items, handle normally
            event.stopPropagation();
            event.preventDefault();

            // Close all open submenus first - this ensures other menu items work
            const openSubmenus = document.querySelectorAll('[data-menu-container]');
            openSubmenus.forEach(container => {
              const nestedMenuItem = container.closest('[data-nested-menu-item]');
              if (nestedMenuItem) {
                // Trigger close event on nested menu items
                const closeEvent = new CustomEvent('closeSubmenu');
                nestedMenuItem.dispatchEvent(closeEvent);
              }
            });

            // Execute the menu item action
            if (menuItem.props.onClick) {
              menuItem.props.onClick(event);
            }

            // Close the main menu if needed
            if (!keepOpenGlobal && !keepOpenLocal) {
              handleClose(event);
            }
          }
        },
        children: props.menu
          ? React.Children.map(props.menu, renderMenu)
          : props.children
      });
    };

    return (
      <>
        {React.cloneElement(trigger, {
          onClick: isOpen ? handleForceClose : handleOpen,
          ref: anchorRef
        })}

        <Menu
          PaperProps={{ 
            sx: { 
              minWidth: minWidth ?? 0,
              pointerEvents: 'auto'
            } 
          }}
          anchorEl={isOpen}
          open={!!isOpen}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{
            zIndex: 9999,
            pointerEvents: 'auto',
            '& .MuiBackdrop-root': {
              pointerEvents: 'none' // Allow clicking through backdrop
            },
            ...sx
          }}
        >
          {React.Children.map(menu, renderMenu)}
        </Menu>
      </>
    );
  }
);

export const DropdownMenuItem = styled(MenuItem)`
  display: flex;
  justify-content: space-between !important;

  & > svg {
    margin-left: 32px;
  }
`;

export const DropdownNestedMenuItem = styled(NestedMenuItem)`
  display: flex;
  justify-content: space-between !important;

  & > svg {
    margin-left: 32px;
  }
`;
